import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { GetOpdEntryById } from "../../../../api/opd/opdEntryAPI";
import { UncontrolledTooltip } from "reactstrap";
import formatDateTime, { formatDate } from "../../../../utils/dateFormatter";
import { IMAGE_URL } from "../../../../utils/api-config";

const OpdReceipt = () => {
  const { id } = useParams();
  const [opd, setOpd] = useState(null);
  const printRef = useRef();
  const letterheadRef = useRef();

  useEffect(() => {
    fetchOpd();
  }, []);

  const fetchOpd = async () => {
    const res = await GetOpdEntryById(id);
    if (res.success) {
      setOpd(res.data);
    }
  };

  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;

    const printWindow = window.open("", "", "height=600");

    printWindow.document.write(`
    <html>
      <head>
        <title>OPD Receipt</title>
        <style>
          @page {
            margin: 1cm;
          }
          body {
            margin: 1cm;
            font-family: times new roman;
            font-size: 14px;
          }
          table {
            width: 100%;
          }
        </style>
      </head>
      <body>
        ${printContents}
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            };
          }
        </script>
      </body>
    </html>
  `);

    printWindow.document.close();
  };

  const handleLetterheadPrint = () => {
    const printContents = letterheadRef.current.innerHTML;

    const printWindow = window.open("", "", "height=600");

    printWindow.document.write(`
    <html>
      <head>
        <title>Prescription</title>
        <style>
          @page {
            size: A4;
            margin: 0;
          }

          body {
            margin: 0;
            font-family: 'Times New Roman';
            font-size: 14px;
          }

          .content {
            padding-top: 240px;   /* 🔥 Adjust this for vertical alignment */
            padding-left: 330px;  /* 🔥 Adjust this for right side column alignment */
            padding-right: 40px;
          }

          table {
            width: 100%;
            border-collapse: collapse;
          }

          td {
            padding: 4px 0;
          }
          hr {
            border: none;
            border-top: 1px solid #000;   /* 🔥 Dark line */
            margin: 10px 0;
          }
        </style>
      </head>
      <body>
        <div class="content">
          ${printContents}
        </div>

        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            };
          };
        </script>
      </body>
    </html>
  `);

    printWindow.document.close();
  };



  if (!opd) return <p>Loading...</p>;

  const grossTotal = opd.amount || 0;
  const discount = opd.discountAmount || 0;
  const netAmount = opd.netAmount || grossTotal - discount;

  return (
    <>
      {/* ACTION BUTTON */}
      <div
        style={{
          textAlign: "right",
          marginTop: "100px",
          marginBottom: "15px"
        }}
      >
        <button
          type="button"
          id="print"
          className="btn btn-info me-2"
          onClick={handlePrint}
        >
          <i className="fa fa-print" /> Print Receipt
        </button>
        <UncontrolledTooltip placement="top" target="print">
          Print Receipt
        </UncontrolledTooltip>
        <button
          type="button"
          id="letterhead-print"
          className="btn btn-success me-2"
          onClick={handleLetterheadPrint}
        >
          <i className="fa fa-print" /> Print Prescription
        </button>
        <UncontrolledTooltip placement="top" target="letterhead-print">
          Print Prescription
        </UncontrolledTooltip>
        <Link to={`/opd/transaction/opd-entry/list`}>
          <button className="btn btn-primary" id="list">
            List OPD Entry
          </button>
          <UncontrolledTooltip placement="top" target="list">
            List OPD Entry
          </UncontrolledTooltip>
        </Link>
      </div>

      {/* RECEIPT */}
      <div
        ref={printRef}
        style={{
          width: "90%",
          margin: "auto",
          fontFamily: "times new roman",
          fontSize: "14px"
        }}
      >
        {/* HEADER */}
        <table width="100%" style={{ borderCollapse: "collapse" }}>
          <tbody>
            <tr>
              {/* 1. Logo Cell */}
              <td width="10%" style={{ textAlign: "right" }}>
                <img
                  src={`${IMAGE_URL}${opd.companyId?.logo}`}
                  alt="logo"
                  style={{ width: "150px" }}
                />
              </td>

              {/* 2. Perfectly Centered Text */}
              <td style={{ textAlign: "center" }}>
                <h2 style={{ margin: 0 }}>{opd.companyId?.name}</h2>
                <h5>{opd.companyId?.address}</h5>
                <h5>({opd.companyId?.description || "Healthcare institution providing specialized medical, nursing, and diagnostic care to patients."})</h5>
                <strong>OPD RECEIPT</strong>
              </td>

              {/* 3. Buffer Cell (Same width as logo cell to balance the center) */}
              <td width="10%"></td>
            </tr>
          </tbody>
        </table>

        <br />

        {/* BASIC INFO */}
        <table width="100%" style={{ borderCollapse: "collapse" }}>
          <tbody>

            <tr>
              <td width="20%" style={{ fontWeight: "bold" }}>Receipt No</td>
              <td width="30%">: {opd.opdNo}</td>

              <td width="25%" style={{ fontWeight: "bold" }}>Date & Time</td>
              <td width="25%">: {formatDate(opd.date)} {opd.time}</td>
            </tr>

            <tr>
              <td style={{ fontWeight: "bold" }}>UHID No</td>
              <td>: {opd.patientId?.uhidNo}</td>

              <td style={{ fontWeight: "bold" }}>OPD No</td>
              <td>: {opd.opdNo}</td>
            </tr>

            <tr>
              <td style={{ fontWeight: "bold" }}>Patient Name</td>
              <td>: {opd.patientId?.patientName}</td>

              <td style={{ fontWeight: "bold" }}>Age / Sex</td>
              <td>
                : {opd.patientId?.age} {opd.patientId?.ageSuffix} / {opd.patientId?.sex}
              </td>
            </tr>

            <tr>
              <td style={{ fontWeight: "bold" }}>Consultant</td>
              <td>: {opd.consultantDoctorId?.name}</td>

              <td style={{ fontWeight: "bold" }}>Valid Upto</td>
              <td>
                : {formatDate(
                  new Date(
                    new Date(opd.date).getTime() +
                    (opd.consultantDoctorId?.opdValidUpTo || 0) * 86400000
                  )
                )}
              </td>
            </tr>

            <tr>
              <td style={{ fontWeight: "bold" }}></td>
              <td></td>

              <td style={{ fontWeight: "bold" }}>Serial No.</td>
              <td>: {opd.opdNo}</td>
            </tr>

          </tbody>
        </table>


        <hr />

        {/* CHARGES */}
        <table width="100%">
          <tbody>
            <tr>
              <td>
                <strong>Charge Name</strong>
              </td>
              <td style={{ textAlign: "right" }}>
                <strong>Amount</strong>
              </td>
            </tr>
            <tr>
              <td>OPD Registration Fee</td>
              <td style={{ textAlign: "right" }}>
                {grossTotal.toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>

        <hr />

        {/* TOTALS */}
        <table width="100%">
          <tbody>
            <tr>
              <td>
                <strong>Payment Mode:</strong> {opd.paymentMode}
              </td>
              <td style={{ textAlign: "right" }}>
                <strong>Gross Total:</strong> {grossTotal.toFixed(2)}
              </td>
            </tr>
            <tr>
              <td>

              </td>
              <td style={{ textAlign: "right" }}>
                <strong>Discount:</strong> {discount}
              </td>
            </tr>
            <tr>
              <td>

              </td>
              <td style={{ textAlign: "right", fontWeight: "bold" }}>
                <strong>Net Amount:</strong> {netAmount.toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>

        <br />

        {/* FOOTER */}
        <table width="100%">
          <tbody>
            <tr>
              <td>
                <strong>Prepared By:</strong> Admin
              </td>
              <td style={{ textAlign: "right" }}>
                <strong>Authorized Signatory</strong>
              </td>
            </tr>
          </tbody>
        </table>

        <hr />

        <p style={{ textAlign: "end", fontSize: "12px" }}>
          Printed On: {formatDateTime(new Date())}
        </p>
        <p style={{ textAlign: "end", fontSize: "12px" }}>
          Not Valid For Medicolegal Purposes
        </p>
      </div>

      {/* LETTERHEAD PRINT SECTION */}
      <div
        ref={letterheadRef}
        style={{
          display: "none",
          fontFamily: "times new roman",
          fontSize: "14px"
        }}
      >
        <hr />
        <table style={{ width: "100%" }}>
          <tbody>

            <tr>
              <td width={"20%"} style={{ fontWeight: "bold" }}>OPD No </td>
              <td width={"30%"}>: {opd.opdNo}</td>

              <td width={"20%"} style={{ fontWeight: "bold" }}>Date </td>
              <td width={"30%"}>: {formatDate(opd.date)}</td>
            </tr>

            <tr>
              <td style={{ fontWeight: "bold" }}>Patient </td>
              <td>: {opd.patientId?.patientName}</td>

              <td style={{ fontWeight: "bold" }}>Age / Sex </td>
              <td>
                : {opd.patientId?.age} {opd.patientId?.ageSuffix} / {opd.patientId?.sex}
              </td>
            </tr>

            <tr>
              <td style={{ fontWeight: "bold" }}>Guardian </td>
              <td>: {opd.patientId?.guardianName}</td>

              <td style={{ fontWeight: "bold" }}>Valid Upto </td>
              <td>
                : {formatDate(
                  new Date(
                    new Date(opd.date).getTime() +
                    (opd.consultantDoctorId?.opdValidUpTo || 0) * 86400000
                  )
                )}
              </td>
            </tr>

            <tr>
              <td style={{ fontWeight: "bold" }}>Serial No </td>
              <td>: {opd.opdNo}</td>

              <td style={{ fontWeight: "bold" }}>UHID </td>
              <td>: {opd.patientId?.uhidNo}</td>
            </tr>

            <tr>
              <td style={{ fontWeight: "bold" }}>Address </td>
              <td>: {opd.patientId?.address}</td>

              <td style={{ fontWeight: "bold" }}>Phone No </td>
              <td>: {opd.patientId?.phoneNo}</td>
            </tr>

          </tbody>
        </table>
        <hr />

      </div>



    </>
  );
};

export default OpdReceipt;
